import { Component, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { Observable, map } from 'rxjs';
import { CoinTransaction } from '../../../models/CoinTransaction';
import { getCoinTransactionsByCustomerNo } from '../../../state/coin-transaction/coin-transaction.actions';
import { selectCoinTransactions } from '../../../state/coin-transaction/coin-transaction.selector';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { HeaderAction, PageHeaderComponent } from "../../page-header/page-header.component";

@Component({
  selector: 'app-transaction-history',
  templateUrl: './transaction-history.component.html',
  styleUrl: './transaction-history.component.css',
  standalone: true,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    PageHeaderComponent
]
})
export class TransactionHistoryComponent implements OnInit {
  transactions$: Observable<CoinTransaction[]>;
  filteredTransactions$: Observable<CoinTransaction[]> = new Observable<CoinTransaction[]>();
  totalCoins$: Observable<number> = new Observable<number>();
  customerNo: string | null = null;
  availableYears: number[] = [];
  selectedYear: number = new Date().getFullYear();
  headerActions: HeaderAction[] = [];

  constructor(private store: Store, private route: ActivatedRoute) {
    this.transactions$ = this.store.pipe(
      select(selectCoinTransactions),
      map(transactions => transactions || [])
    );

    this.headerActions = [
      {
        label: 'Exportieren als CSV',
        icon: 'download',
        onClick: () => this.exportToCSV(),
        variant: 'primary'
      }
    ];
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.customerNo = params.get('id');
      if (this.customerNo) {
        this.store.dispatch(getCoinTransactionsByCustomerNo({ customerNo: +this.customerNo }));
      }
    });
    this.setupTransactionStreams();
  }

  private setupTransactionStreams(): void {
    // Extract available years and filter transactions
    this.filteredTransactions$ = this.transactions$.pipe(
      map((transactions) => {
        // Extract available years
        const years = new Set<number>();
        transactions.forEach(tx => {
          if (tx.createdDate) {
            years.add(new Date(tx.createdDate).getFullYear());
          }
        });
        this.availableYears = Array.from(years).sort((a, b) => b - a);
        
        if (this.availableYears.length > 0 && !this.selectedYear) {
          this.selectedYear = this.availableYears[0];
        }

        // Filter by selected year
        return transactions.filter(tx => {
          if (!tx.createdDate) return false;
          return new Date(tx.createdDate).getFullYear() === this.selectedYear;
        });
      })
    );

    // Calculate total coins
    this.totalCoins$ = this.filteredTransactions$.pipe(
      map(transactions => 
        transactions.reduce((sum, tx) => sum + (tx.amount || 0), 0)
      )
    );
  }

  onYearChange(year: number): void {
    this.selectedYear = year;
    this.setupTransactionStreams();
  }

  back(): void {
    window.history.back();
  }

  exportToCSV() {
    this.filteredTransactions$.subscribe(async transactions => {
      if (!transactions || transactions.length === 0) return;
      
      // Calculate total
      const total = transactions.reduce((sum, tx) => sum + (tx.amount || 0), 0);
      
      const header = ['Kunde','Datum', 'Grund', 'Auftragsnummer', 'Coins', 'Gewährt von'];
      const rows = transactions.map(tx => [
        tx.customer?.userDetails?.firstName + " " + tx.customer?.userDetails?.lastName || '',
        tx.createdDate ? new Date(tx.createdDate).toLocaleString() : '',
        tx.reason!.reasonText || '',
        tx.reflexOrderNo || '',
        tx.amount || '',
        tx.reason?.reasonText?.includes('Generiert') ? 'Niclen GmbH' : (tx.grantedBy!.firstName + ' ' + tx.grantedBy!.lastName || '')
      ]);
      
      // Add total row
      const totalRow = ['', '', '', `Summe ${this.selectedYear}`, total, ''];
      
      const csvContent = [header, ...rows, totalRow]
        .map(e => e.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      if ('showSaveFilePicker' in window) {
        // @ts-ignore
        const handle = await window.showSaveFilePicker({
          suggestedName: 'coin-transactions.csv',
          types: [{ description: 'CSV file', accept: { 'text/csv': ['.csv'] } }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        // Fallback for browsers that don't support the API
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'coin-transactions.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }).unsubscribe();
  }
}
